import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, number, ref } from "yup";
import { sendCode } from "../api";
import { createAdminUser, resetStatus } from "../redux/usersSagas";
import { useError } from "../hooks/useError";
import { MainModal } from "../../../../components/modal/MainModal";
import LoadingAnimation from "../../../../components/loadingAnimation/loadingAnimation";
import { Input, PasswordInput } from "../../../../components/form/Input/Input";
import { ErrorList } from "../../../../components/ErrorList";
import { generatePassword, passwordSchemaTest } from "../../../../utils/password-utils";
import { showToast } from "../../../../utils/showToast";

const schema = object({
  first_name: string().label("First Name").trim().min(2).required(),
  last_name: string().label("Last Name").trim().min(2).required(),
  email: string().label("Email").trim().email().required(),
  password: string().label("Password").min(6).max(40).test(passwordSchemaTest).required(),
  password_confirmation: string()
    .label("Password Confirmation")
    .oneOf([ref("password")], "${path} must match Password")
    .required(),
  verification_code: number()
    .label("Verification Code")
    .integer("Please enter numbers")
    .typeError("Please enter numbers")
}).required();

const generatedPassword = generatePassword();

export const AddAdmin = ({ onClose }) => {
  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      send_welcome_email: false,
      password: generatedPassword,
      password_confirmation: generatedPassword
    }
  });

  const [isVerificationPhase, setVerificationPhase] = useState(false);

  const dispatch = useDispatch();

  const close = () => {
    dispatch(resetStatus());
    onClose();
  };

  const { errors: serverErrors, status } = useError(() => {
    if (!isVerificationPhase) {
      setVerificationPhase(true);
    } else {
      showToast("User was successfully created");
      close();
    }
  });

  const onSubmit = async (data) => {
    const trimmedData = { ...data, phone_number: data.phone_number?.trim() };
    if (!isVerificationPhase) {
      dispatch(createAdminUser({ data: trimmedData, type: "confirm" }));
    } else {
      let isValid = trigger("verification_code");
      if (isValid) {
        dispatch(createAdminUser({ data: trimmedData }));
      }
    }
  };

  const primaryButtonDisabled = isSubmitting || status == "pending";

  return (
    <>
      <MainModal
        isOpen
        onClose={close}
        parentSelector=".admin-users"
        headerText="Add Admin User"
        headerCloseHandler={close}
        bodyClassName="main-popup-body"
        primaryButtonText="Save"
        primaryButtonDisabled={primaryButtonDisabled}
        primaryBtnHandler={handleSubmit(onSubmit)}
        secondaryBtnText="Cancel"
        secondaryBtnHandler={close}
        height="auto"
        width={900}
      >
        <div>
          {isVerificationPhase ? (
            <div className="admin-users__verification">
              <Input
                label="Enter your Verification Code:"
                htmlFor="verification_code"
                customProps={register("verification_code")}
              />
              <button
                className="button button--secondary button--no-outline"
                onClick={() => sendCode("sms")}
              >
                Resend Verification Code (SMS)
              </button>
              <button
                className="button button--secondary button--no-outline"
                onClick={() => sendCode("email")}
              >
                Resend Verification Code (EMAIL)
              </button>
            </div>
          ) : (
            <>
              <div className="admin-users__section">
                <Input
                  label="First Name"
                  htmlFor="first_name"
                  customProps={register("first_name")}
                />
                <Input label="Last Name" htmlFor="last_name" customProps={register("last_name")} />
                <Input label="Email" htmlFor="email" customProps={register("email")} />
              </div>

              <div className="admin-users__section">
                <PasswordInput
                  label="Password"
                  customProps={register("password")}
                  htmlFor="password"
                />
                <PasswordInput
                  label="Password Confirmation"
                  customProps={register("password_confirmation")}
                  htmlFor="password_confirmation"
                />
                <Input
                  label="Phone number"
                  htmlFor="phone_number"
                  placeholder="+1 (999) 999 9999"
                  customProps={register("phone_number")}
                />
              </div>
            </>
          )}

          {status === "pending" && <LoadingAnimation uiLoader />}
          <ErrorList errors={{ ...errors, ...serverErrors }} />
        </div>
      </MainModal>
    </>
  );
};
